import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Zap, Copy, X } from "lucide-react";
import { toast } from "sonner";

/** Public flash-sale banner. Fetches active flash sales from
 *  /api/promotions/active and renders the closest-to-expiry one with a live
 *  countdown. Users can dismiss the banner for the session. */
export default function FlashSaleBanner() {
  const [sale, setSale] = useState(null);
  const [remain, setRemain] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined"
      ? sessionStorage.getItem("dailybean_flash_dismissed") === "1"
      : false
  );

  useEffect(() => {
    if (dismissed) return;
    api.get("/promotions/active?kind=flash_sale").then((r) => {
      const list = r.data || [];
      // Prefer sales with an explicit expiry, sorted by soonest
      const withEnd = list.filter((s) => s.expires_at)
        .sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));
      setSale(withEnd[0] || list[0] || null);
    }).catch(() => {});
  }, [dismissed]);

  useEffect(() => {
    if (!sale?.expires_at) return;
    const tick = () => {
      const diff = new Date(sale.expires_at) - new Date();
      if (diff <= 0) { setRemain(null); setSale(null); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemain({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sale]);

  if (dismissed || !sale) return null;

  const dismiss = () => {
    sessionStorage.setItem("dailybean_flash_dismissed", "1");
    setDismissed(true);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(sale.code);
    toast.success(`Coupon ${sale.code} copied — paste it at checkout!`);
  };

  const discountLabel =
    sale.discount_type === "percentage" ? `${sale.value}% off`
    : sale.discount_type === "flat" ? `₹${sale.value} off`
    : "Free shipping";

  return (
    <div
      data-testid="flash-sale-banner"
      className="relative overflow-hidden bg-gradient-to-r from-[#3B2F2F] via-[#993322] to-[#D4AF37] text-white">
      <div className="pattern-beans absolute inset-0 opacity-10" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3 relative z-10">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0 animate-pulse">
            <Zap className="w-5 h-5 text-[#FDE68A]" />
          </div>
          <div className="min-w-0">
            <div className="font-outfit font-bold text-sm md:text-base truncate">
              {sale.banner_message || sale.title || `${discountLabel} — limited time`}
            </div>
            <div className="text-[11px] text-white/70">
              Use code <span className="font-mono font-bold text-[#FDE68A]">{sale.code}</span> · {discountLabel}
              {sale.min_order > 0 && ` · Min order ₹${sale.min_order}`}
            </div>
          </div>
        </div>

        {remain && (
          <div className="hidden sm:flex items-center gap-1.5" data-testid="flash-countdown">
            {remain.d > 0 && <Unit label="D" v={remain.d} />}
            <Unit label="H" v={remain.h} />
            <Unit label="M" v={remain.m} />
            <Unit label="S" v={remain.s} />
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={copyCode}
            data-testid="flash-copy-code"
            className="bg-white text-[#3B2F2F] rounded-full px-3 py-1.5 text-xs font-outfit font-bold flex items-center gap-1.5 hover:bg-[#FDE68A]">
            <Copy className="w-3 h-3" /> {sale.code}
          </button>
          <button
            onClick={dismiss}
            data-testid="flash-dismiss"
            aria-label="Dismiss"
            className="text-white/70 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Unit({ label, v }) {
  return (
    <div className="bg-black/25 rounded-md px-2 py-0.5 font-mono text-xs min-w-[36px] text-center">
      <span className="font-bold">{String(v).padStart(2, "0")}</span>
      <span className="text-white/60 ml-0.5 text-[10px]">{label}</span>
    </div>
  );
}
